/**
 * Department view — `/<key>/work` (#1229, I2 of
 * engineering/plans/project-department-dashboard.yaml; design
 * sessions/1222/session-01/design-1222-department-dashboard.md Q2, Q4, Q5,
 * Q7, as ruled by Review #1225 and the coordinator's comment on #1229).
 *
 * One component, parameterised by the department key, renders all ten views
 * from GET /api/departments/:key (#1227). It answers Chris's four questions
 * (plan, acceptance.reviewer_brief) on one page, with nothing behind a click
 * or a hover — the S3 reviewers see still screenshots (design Q6):
 *
 *   1. Tasks    — the department's live Command Center to-dos (Status as
 *                 written, assignee, due date, `Snoozed until <date>`), and
 *      Tickets  — its open issues labelled `ticket`;
 *   2. Issues   — its open issues not labelled `ticket`;
 *   3. each issue's `## Type:` kind, `Project:` line and filer (Q7), each
 *                 under its own label, or the text saying the body has none;
 *   4. Closed   — issues, tickets and to-dos closed in the 7-day Pacific
 *                 window, newest first, with what happened (Q5 rules 1–3).
 *
 * Open tickets and issues come in issue-number order, highest first, as
 * the wire sends them; to-dos are sorted newest first by Created here. Each section shows its own read stamp and, when its read failed, its
 * own failure text; one failing never hides the other.
 *
 * Words on screen are the record's own (plan guard): the repo's name,
 * `ticket`, the Type value as written, the Project line as written, GitHub's
 * close reason as given, and the Tasks.Component value as written. Nothing on
 * this page says an item is late, stale, stuck, healthy or behind — no colour,
 * sort or heading carries such a judgment.
 *
 * Reads / Horizon / Stories (command-center/CONTRACT.md, Declarations): this
 * page draws GET /api/departments/:key and counts nothing of its own; the
 * route's declaration (apps/worker/src/routes/departments.ts) covers it.
 */

import type { ReactNode } from "react";
import { Card } from "@/components/ui/card";
import { useDepartmentWork } from "@/lib/use-department-work";
import { fmtDate } from "@/lib/format";
import {
  dateInPT,
  departmentByKey,
  type DepartmentClosedIssue,
  type DepartmentClosedTask,
  type DepartmentFiler,
  type DepartmentGithubSection,
  type DepartmentIssue,
  type DepartmentTask,
  type DepartmentTasksSection,
  type DepartmentWorkResponse,
} from "@command-center/shared";

/** The section heading Chris's own three words name (design Q2). */
export const DEPARTMENT_VIEW_LABEL = "Tasks, tickets and issues";

/**
 * "2026-10-13 10:37 PM PT" — an instant's Pacific day (`dateInPT`, the
 * Contract's day boundary) and clock time. The same shape as the worker's
 * failure texts (adapter-github department-reader.ts `formatInstantPT`), so
 * every stamp on one screen reads alike; the day is printed because a kept
 * snapshot can be from an earlier day.
 */
export function fmtPacificInstant(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  const day = dateInPT(iso) ?? iso;
  const clock = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(ms));
  return `${day} ${clock} PT`;
}

/** A date-only value (due, snoozed, completed, the window's days) through the
 *  web's day rule, `dayZone` in lib/format.ts: "Oct 7, 2026". */
function fmtDay(ymd: string): string {
  return fmtDate(ymd, { year: true }) || ymd;
}

export function DepartmentWorkPage({ departmentKey }: { departmentKey: string }) {
  const department = departmentByKey(departmentKey);
  const query = useDepartmentWork(departmentKey);
  const label = department?.label ?? departmentKey;

  return (
    <section
      className="mx-auto max-w-5xl p-6"
      data-testid={`department-work-${departmentKey}`}
    >
      <h1 className="mb-1 text-xl tracking-tight">{label}</h1>
      <p className="mb-5 text-sm text-plane-muted">{DEPARTMENT_VIEW_LABEL}</p>

      {query.isLoading ? (
        <p className="p-8 text-sm text-plane-muted" data-testid="department-loading">
          Loading…
        </p>
      ) : query.isError ? (
        <p className="p-8 text-sm text-plane-foreground" data-testid="department-error">
          The page could not show /api/departments/{departmentKey}:{" "}
          {query.error.message}. It asks again in 5 minutes.
        </p>
      ) : query.data ? (
        <DepartmentWork data={query.data} />
      ) : null}
    </section>
  );
}

function DepartmentWork({ data }: { data: DepartmentWorkResponse }) {
  const { github, tasks, department } = data;
  return (
    <div className="flex flex-col gap-8">
      <Stamps github={github} tasks={tasks} repo={department.repo} repoUrl={department.url} />
      <TasksSection tasks={tasks} />
      <TicketsSection github={github} />
      <IssuesSection github={github} />
      <ClosedSection github={github} tasks={tasks} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Read stamps — when each half was read, at the top of the page.
// ---------------------------------------------------------------------------

function Stamps({
  github,
  tasks,
  repo,
  repoUrl,
}: {
  github: DepartmentGithubSection;
  tasks: DepartmentTasksSection;
  repo: string;
  repoUrl: string;
}) {
  return (
    <div className="flex flex-col gap-1 text-sm text-plane-muted" data-testid="department-stamps">
      <div>
        Repo:{" "}
        <a className="underline" href={repoUrl} target="_blank" rel="noreferrer">
          {repo}
        </a>
      </div>
      {github.state === "read" ? (
        <div data-testid="department-github-stamp">
          Read from GitHub at {fmtPacificInstant(github.readAt)}; the snapshot reached
          Command Center at {fmtPacificInstant(github.receivedAt)}.
        </div>
      ) : github.state === "failed" ? (
        <div data-testid="department-github-stamp">
          GitHub read attempted at {fmtPacificInstant(github.attemptedAt)}; the attempt
          reached Command Center at {fmtPacificInstant(github.receivedAt)}.
        </div>
      ) : (
        <div data-testid="department-github-stamp">{github.text}</div>
      )}
      {tasks.state === "read" ? (
        <div data-testid="department-tasks-stamp">
          Command Center tasks read at {fmtPacificInstant(tasks.readAt)}.
        </div>
      ) : tasks.state === "failed" ? (
        <div data-testid="department-tasks-stamp">
          Command Center tasks read attempted at {fmtPacificInstant(tasks.attemptedAt)}.
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section frame.
// ---------------------------------------------------------------------------

function Section({
  id,
  title,
  note,
  children,
}: {
  id: string;
  title: string;
  note?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section data-testid={`department-section-${id}`}>
      <h2 className="mb-1 text-base font-semibold tracking-tight text-plane-foreground">
        {title}
      </h2>
      {note ? <p className="mb-2 text-xs text-plane-muted">{note}</p> : null}
      {children}
    </section>
  );
}

function Observation({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <p className="text-sm text-plane-muted" data-testid={testId}>
      {children}
    </p>
  );
}

function GithubNotRead({ github }: { github: DepartmentGithubSection }) {
  if (github.state === "failed") {
    return <Observation testId="department-github-failed">{github.text}</Observation>;
  }
  if (github.state === "absent") {
    return <Observation testId="department-github-absent">{github.text}</Observation>;
  }
  return null;
}

// ---------------------------------------------------------------------------
// 1. Tasks — live Command Center to-dos under this department (design Q4).
// ---------------------------------------------------------------------------

function TasksSection({ tasks }: { tasks: DepartmentTasksSection }) {
  if (tasks.state === "none") {
    return (
      <Section id="tasks" title="Command Center tasks">
        <Observation testId="department-tasks-none">{tasks.text}</Observation>
      </Section>
    );
  }
  if (tasks.state === "failed") {
    return (
      <Section id="tasks" title="Command Center tasks">
        <Observation testId="department-tasks-failed">{tasks.text}</Observation>
      </Section>
    );
  }
  const components = tasks.components.join(", ");
  // Newest first by Created — the order they were filed in (design Q2); the
  // wire sends the to-dos in Airtable's order, so the page sorts them.
  const open = [...tasks.open].sort((a, b) => b.created.localeCompare(a.created));
  return (
    <Section
      id="tasks"
      title={`Command Center tasks (${open.length})`}
      note={`Live to-dos (Completed blank) whose Component is ${components}, snoozed ones included, newest first.`}
    >
      {open.length === 0 ? (
        <Observation testId="department-tasks-empty">
          No live Command Center to-do has Component {components}.
        </Observation>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {open.map((t) => (
            <TaskRow key={t.id} task={t} />
          ))}
        </ul>
      )}
    </Section>
  );
}

function TaskRow({ task }: { task: DepartmentTask }) {
  return (
    <li>
      <Card className="px-3 py-2" data-testid={`department-task-${task.id}`}>
        <div className="text-sm font-medium leading-snug">{task.title}</div>
        <Meta
          items={[
            `Status: ${task.status}`,
            `Assignee: ${task.assignee ?? "none"}`,
            task.dueBy ? `Due: ${fmtDay(task.dueBy)}` : "No due date",
            task.snoozedUntil ? `Snoozed until ${fmtDay(task.snoozedUntil)}` : null,
            task.reference ? `Reference: ${task.reference}` : null,
          ]}
        />
      </Card>
    </li>
  );
}

// ---------------------------------------------------------------------------
// 1b. Tickets — open issues labelled `ticket`.
// ---------------------------------------------------------------------------

function TicketsSection({ github }: { github: DepartmentGithubSection }) {
  if (github.state !== "read") {
    return (
      <Section id="tickets" title="Tickets">
        <GithubNotRead github={github} />
      </Section>
    );
  }
  return (
    <Section
      id="tickets"
      title={`Tickets (${github.openTickets.length} open)`}
      note="Open issues labelled ticket, highest number first."
    >
      {github.openTickets.length === 0 ? (
        <Observation testId="department-tickets-empty">
          No open issue carries the label ticket.
        </Observation>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {github.openTickets.map((i) => (
            <IssueRow key={i.number} issue={i} />
          ))}
        </ul>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 2–3. Issues — open, not labelled `ticket`, with kind, plan and filer.
// ---------------------------------------------------------------------------

function IssuesSection({ github }: { github: DepartmentGithubSection }) {
  if (github.state !== "read") {
    return (
      <Section id="issues" title="Issues">
        <GithubNotRead github={github} />
      </Section>
    );
  }
  return (
    <Section
      id="issues"
      title={`Issues (${github.openIssues.length} open)`}
      note="Open issues not labelled ticket, highest number first. Each shows its ## Type: line, its Project: line (the plan it names) and who filed it, as the issue body writes them."
    >
      {github.openIssues.length === 0 ? (
        <Observation testId="department-issues-empty">
          No open issue without the label ticket.
        </Observation>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {github.openIssues.map((i) => (
            <IssueRow key={i.number} issue={i} />
          ))}
        </ul>
      )}
    </Section>
  );
}

function filerText(filer: DepartmentFiler): string {
  switch (filer.source) {
    case "filed_by":
      return `Filed by: ${filer.text}`;
    case "origin":
      return `Origin: ${filer.text}`;
    case "none":
      return "No Filed by: line and no ## Origin section";
  }
}

function issueMeta(issue: DepartmentIssue): (string | null)[] {
  return [
    issue.kind ? `Type: ${issue.kind}` : "No ## Type: line",
    issue.project ? `Project: ${issue.project}` : "No Project: line",
    filerText(issue.filer),
    `Opened ${fmtPacificInstant(issue.createdAt)}`,
  ];
}

function IssueTitle({ issue }: { issue: DepartmentIssue }) {
  return (
    <a
      className="text-sm font-medium leading-snug hover:underline"
      href={issue.url}
      target="_blank"
      rel="noreferrer"
    >
      <span className="text-muted-foreground">#{issue.number}</span> {issue.title}
      {issue.isTicket ? (
        <span className="ml-2 rounded-chip border px-1.5 py-0.5 text-[11px] font-normal text-plane-muted">
          ticket
        </span>
      ) : null}
    </a>
  );
}

function IssueRow({ issue }: { issue: DepartmentIssue }) {
  return (
    <li>
      <Card className="px-3 py-2" data-testid={`department-issue-${issue.number}`}>
        <IssueTitle issue={issue} />
        <Meta items={issueMeta(issue)} />
      </Card>
    </li>
  );
}

// ---------------------------------------------------------------------------
// 4. Closed — the 7-day Pacific window, newest first, what happened (Q5).
// ---------------------------------------------------------------------------

function windowText(w: { start: string; end: string }): string {
  return `${fmtDay(w.start)} – ${fmtDay(w.end)} (Pacific days)`;
}

function ClosedSection({
  github,
  tasks,
}: {
  github: DepartmentGithubSection;
  tasks: DepartmentTasksSection;
}) {
  const win =
    github.state === "read" ? github.window : tasks.state === "read" ? tasks.window : null;
  return (
    <Section
      id="closed"
      title={win ? `Closed ${windowText(win)}` : "Closed in the last 7 days"}
    >
      <div className="flex flex-col gap-5">
        <div>
          <h3 className="mb-1 text-sm font-semibold text-plane-foreground">
            {github.state === "read"
              ? `Issues and tickets closed (${github.closed.length})`
              : "Issues and tickets closed"}
          </h3>
          {github.state !== "read" ? (
            <GithubNotRead github={github} />
          ) : github.closed.length === 0 ? (
            <Observation testId="department-closed-empty">
              No issue or ticket closed in {windowText(github.window)}.
            </Observation>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {github.closed.map((i) => (
                <ClosedIssueRow key={i.number} issue={i} branch={github.defaultBranch} readAt={github.readAt} />
              ))}
            </ul>
          )}
        </div>
        <ClosedTasks tasks={tasks} />
      </div>
    </Section>
  );
}

function ClosedIssueRow({
  issue,
  branch,
  readAt,
}: {
  issue: DepartmentClosedIssue;
  branch: string;
  readAt: string;
}) {
  const wh = issue.whatHappened;
  return (
    <li>
      <Card className="px-3 py-2" data-testid={`department-closed-${issue.number}`}>
        <IssueTitle issue={issue} />
        <Meta
          items={[
            `Closed ${fmtPacificInstant(issue.closedAt)}`,
            ...issueMeta(issue).slice(0, 3),
          ]}
        />
        <div className="mt-1 flex flex-col gap-0.5 text-[13px] leading-snug text-plane-foreground">
          {wh.source === "close_summary" ? (
            <>
              <div>
                <span className="font-medium">Close summary (/stop), {fmtPacificInstant(wh.commentedAt)}:</span>{" "}
                What: {wh.what}
              </div>
              <Links pr={wh.pr} record={wh.record} />
            </>
          ) : wh.source === "ticket_done" ? (
            <>
              <div>
                <span className="font-medium">Ticket comment, {fmtPacificInstant(wh.commentedAt)}:</span>{" "}
                Done: {wh.done}
              </div>
              {wh.where ? <div>Where: {wh.where}</div> : null}
            </>
          ) : (
            <>
              <div>No Close summary (/stop) or Done: comment. What GitHub shows:</div>
              <div>Close reason: {wh.closeReason ?? "none given"}</div>
              <div>
                {wh.lastComment
                  ? `Last comment (${fmtPacificInstant(wh.lastComment.createdAt)}): ${wh.lastComment.firstLine}`
                  : "No comments"}
              </div>
            </>
          )}
          {wh.source !== "close_summary" ? (
            issue.closeoutLog.present && issue.closeoutLog.url ? (
              <div>
                <a className="underline" href={issue.closeoutLog.url} target="_blank" rel="noreferrer">
                  {issue.closeoutLog.name}
                </a>{" "}
                at the repo root on {branch}
              </div>
            ) : (
              <div>
                No {issue.closeoutLog.name} at the repo root on {branch} at {fmtPacificInstant(readAt)}
              </div>
            )
          ) : null}
        </div>
      </Card>
    </li>
  );
}

function Links({ pr, record }: { pr: string | null; record: string | null }) {
  if (!pr && !record) return null;
  return (
    <div className="flex flex-wrap gap-x-3">
      {pr ? (
        <span>
          PR:{" "}
          <a className="underline" href={pr} target="_blank" rel="noreferrer">
            {pr}
          </a>
        </span>
      ) : null}
      {record ? (
        <span>
          Record:{" "}
          <a className="underline" href={record} target="_blank" rel="noreferrer">
            {record}
          </a>
        </span>
      ) : null}
    </div>
  );
}

function ClosedTasks({ tasks }: { tasks: DepartmentTasksSection }) {
  if (tasks.state === "none") {
    return (
      <div>
        <h3 className="mb-1 text-sm font-semibold text-plane-foreground">Command Center tasks completed</h3>
        <Observation>{tasks.text}</Observation>
      </div>
    );
  }
  if (tasks.state === "failed") {
    return (
      <div>
        <h3 className="mb-1 text-sm font-semibold text-plane-foreground">Command Center tasks completed</h3>
        <Observation>{tasks.text}</Observation>
      </div>
    );
  }
  const components = tasks.components.join(", ");
  return (
    <div>
      <h3 className="mb-1 text-sm font-semibold text-plane-foreground">
        Command Center tasks completed ({tasks.closed.length})
      </h3>
      {tasks.closed.length === 0 ? (
        <Observation testId="department-closed-tasks-empty">
          No Command Center to-do with Component {components} was completed in{" "}
          {windowText(tasks.window)}.
        </Observation>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {tasks.closed.map((t) => (
            <ClosedTaskRow key={t.id} task={t} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ClosedTaskRow({ task }: { task: DepartmentClosedTask }) {
  return (
    <li>
      <Card className="px-3 py-2" data-testid={`department-closed-task-${task.id}`}>
        <div className="text-sm font-medium leading-snug">{task.title}</div>
        <Meta
          items={[
            `Completed ${fmtDay(task.completed)}`,
            `Status: ${task.status}`,
            `Assignee: ${task.assignee ?? "none"}`,
            task.reference ? `Reference: ${task.reference}` : null,
          ]}
        />
      </Card>
    </li>
  );
}

// ---------------------------------------------------------------------------

function Meta({ items }: { items: (string | null)[] }) {
  const shown = items.filter((x): x is string => x !== null);
  return (
    <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-plane-muted">
      {shown.map((text, i) => (
        <span key={i}>{text}</span>
      ))}
    </div>
  );
}
